'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronRightGlyph, SearchGlyph } from '../../../internal/icons.js';
import { Kbd } from '../../typography/kbd/index.js';
import { Spinner } from '../../feedback/spinner/index.js';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '../../overlays/dialog/index.js';
import { formatHotkey, useHotkey } from './command-palette.hotkey.js';
export { formatHotkey, matchesHotkey, isTypingTarget, useHotkey, } from './command-palette.hotkey.js';
const DEFAULT_MESSAGES = {
    title: 'Command palette',
    placeholder: 'Type a command or search…',
    results: 'Results',
    recent: 'Recent',
    empty: 'No results',
    loading: 'Searching…',
    resultCount: (n) => (n === 1 ? '1 result' : `${n} results`),
    sourceError: (group) => `Could not load ${group.toLowerCase()}.`,
    retry: 'Retry',
    back: (page) => `Back from ${page}`,
    navigate: 'to navigate',
    select: 'to select',
    close: 'to close',
};
const NO_ITEMS = [];
const NO_SOURCES = [];
const NO_PAGES = {};
function normalise(text) {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
/** The default ranking: all words must match; label prefix > word start > anywhere > keywords. */
export function defaultCommandFilter(item, query) {
    const words = normalise(query).split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return 1;
    const label = normalise(item.label);
    const rest = normalise([item.description ?? '', ...(item.keywords ?? [])].join(' '));
    let score = 0;
    for (const w of words) {
        if (label.startsWith(w))
            score += 4;
        else if (new RegExp(`(^|[\\s\\-_/.])${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(label))
            score += 3;
        else if (label.includes(w))
            score += 2;
        else if (rest.includes(w))
            score += 1;
        else
            return 0;
    }
    return score;
}
function shortcutKeys(shortcut) {
    return typeof shortcut === 'string' ? formatHotkey(shortcut) : shortcut;
}
/**
 * A ⌘K / Ctrl+K command palette: a modal dialog with a search combobox and a
 * grouped listbox of commands, recent items and results from asynchronous
 * sources, nested pages such as "Move to…", and shortcut hints. The app
 * supplies every command and search; the palette owns keyboard, focus,
 * filtering, debouncing, cancellation and announcements.
 */
export const CommandPalette = forwardRef(function CommandPalette({ items = NO_ITEMS, sources = NO_SOURCES, pages = NO_PAGES, recent = NO_ITEMS, open: openProp, defaultOpen = false, onOpenChange, search: searchProp, onSearchChange, hotkey = 'mod+k', hotkeyInFields = true, filter = defaultCommandFilter, onItemSelect, trigger, footer, emptyState, messages: messagesProp, className, }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const id = useId();
    const listId = `mn-command-palette-${id}-list`;
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const open = openProp ?? internalOpen;
    const setOpen = useCallback((next) => {
        if (openProp === undefined)
            setInternalOpen(next);
        onOpenChange?.(next);
    }, [openProp, onOpenChange]);
    const [internalSearch, setInternalSearch] = useState('');
    const search = searchProp ?? internalSearch;
    const setSearch = (next) => {
        if (searchProp === undefined)
            setInternalSearch(next);
        onSearchChange?.(next);
    };
    /** The stack of open nested pages, by key. */
    const [stack, setStack] = useState([]);
    const pageKey = stack[stack.length - 1] ?? null;
    const page = pageKey ? pages[pageKey] : undefined;
    const [activeId, setActiveId] = useState(null);
    const [sourceStates, setSourceStates] = useState({});
    const [retryCount, setRetryCount] = useState(0);
    useHotkey(hotkey, () => setOpen(!open), { inFields: hotkeyInFields });
    // Every open starts on the first page with an empty query.
    useEffect(() => {
        if (!open)
            return;
        setStack([]);
        setSourceStates({});
        if (searchProp === undefined)
            setInternalSearch('');
        else if (searchProp !== '')
            onSearchChange?.('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const query = search.trim();
    const pageItems = useMemo(() => (page ? (page.items ?? NO_ITEMS) : items), [page, items]);
    const pageSources = useMemo(() => (page ? (page.sources ?? NO_SOURCES) : sources), [page, sources]);
    // Asynchronous sources: debounce, then search with an AbortSignal; a newer
    // query, a page change or closing aborts the request in flight.
    const sourceKey = pageSources.map((s) => s.id).join('\n');
    useEffect(() => {
        if (!open)
            return;
        const controllers = [];
        const timers = [];
        for (const source of pageSources) {
            const min = source.minQueryLength ?? 1;
            if (query.length < min) {
                setSourceStates((s) => s[source.id]?.status === 'idle'
                    ? s
                    : { ...s, [source.id]: { status: 'idle', query, items: [] } });
                continue;
            }
            setSourceStates((s) => ({
                ...s,
                [source.id]: { status: 'loading', query, items: s[source.id]?.items ?? [] },
            }));
            const controller = new AbortController();
            controllers.push(controller);
            timers.push(setTimeout(() => {
                source.search(query, controller.signal).then((results) => {
                    if (controller.signal.aborted)
                        return;
                    setSourceStates((s) => ({
                        ...s,
                        [source.id]: { status: 'done', query, items: results },
                    }));
                }, () => {
                    if (controller.signal.aborted)
                        return;
                    setSourceStates((s) => ({
                        ...s,
                        [source.id]: { status: 'error', query, items: [] },
                    }));
                });
            }, source.debounceMs ?? 200));
        }
        return () => {
            timers.forEach(clearTimeout);
            controllers.forEach((c) => c.abort());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, query, pageKey, sourceKey, retryCount]);
    const sections = useMemo(() => {
        const out = [];
        let n = 0;
        const row = (item) => ({
            item,
            domId: `${listId}-${n++}`,
        });
        if (!page && query === '' && recent.length > 0) {
            out.push({ key: 'recent', heading: messages.recent, rows: recent.map(row) });
        }
        const ranked = pageItems
            .map((item, index) => ({ item, index, score: query === '' ? 1 : filter(item, query) }))
            .filter((r) => r.score > 0);
        const byGroup = new Map();
        for (const r of ranked) {
            const g = r.item.group ?? '';
            const list = byGroup.get(g);
            if (list)
                list.push(r);
            else
                byGroup.set(g, [r]);
        }
        // Ungrouped items first, then groups in the order they first appear.
        const groups = [...byGroup.keys()].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : 0));
        for (const g of groups) {
            const list = (byGroup.get(g) ?? [])
                .slice()
                .sort((a, b) => b.score - a.score || a.index - b.index);
            out.push({
                key: `group:${g}`,
                heading: g || undefined,
                rows: list.map((r) => row(r.item)),
            });
        }
        for (const source of pageSources) {
            const state = sourceStates[source.id];
            if (!state || state.items.length === 0)
                continue;
            out.push({ key: `source:${source.id}`, heading: source.group, rows: state.items.map(row) });
        }
        return out;
    }, [
        page,
        query,
        recent,
        pageItems,
        pageSources,
        sourceStates,
        filter,
        listId,
        messages.recent,
    ]);
    const rows = useMemo(() => sections.flatMap((s) => s.rows), [sections]);
    const enabled = useMemo(() => rows.filter((r) => !r.item.disabled), [rows]);
    const loading = pageSources.some((s) => sourceStates[s.id]?.status === 'loading');
    const failed = pageSources.filter((s) => sourceStates[s.id]?.status === 'error');
    // Keep the active row when it survives a change of results, else the first enabled row.
    const activeRow = enabled.find((r) => r.item.id === activeId) ?? enabled[0] ?? null;
    const activeDomId = activeRow?.domId;
    useEffect(() => {
        if (activeRow && activeRow.item.id !== activeId)
            setActiveId(activeRow.item.id);
    }, [activeRow, activeId]);
    // Typing starts again from the top.
    useEffect(() => {
        setActiveId(null);
    }, [query, pageKey]);
    useEffect(() => {
        if (!activeDomId)
            return;
        const el = listRef.current?.querySelector(`[id="${activeDomId}"]`);
        el?.scrollIntoView?.({ block: 'nearest' });
    }, [activeDomId]);
    const moveActive = (delta) => {
        if (enabled.length === 0)
            return;
        const at = activeRow ? enabled.indexOf(activeRow) : -1;
        const next = enabled[(at + delta + enabled.length) % enabled.length];
        if (next)
            setActiveId(next.item.id);
    };
    const pushPage = (key) => {
        setStack((s) => [...s, key]);
        setSearch('');
        inputRef.current?.focus();
    };
    const popPage = () => {
        setStack((s) => s.slice(0, -1));
        setSearch('');
        inputRef.current?.focus();
    };
    const choose = (item) => {
        if (item.disabled)
            return;
        item.onSelect?.(item);
        onItemSelect?.(item, { page: pageKey });
        if (item.page && pages[item.page])
            pushPage(item.page);
        else if (!item.keepOpen)
            setOpen(false);
    };
    const onKeyDown = (e) => {
        if (e.nativeEvent.isComposing)
            return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                moveActive(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                moveActive(-1);
                break;
            case 'PageDown':
            case 'PageUp': {
                e.preventDefault();
                if (enabled.length === 0)
                    break;
                const at = activeRow ? enabled.indexOf(activeRow) : 0;
                const to = Math.max(0, Math.min(enabled.length - 1, at + (e.key === 'PageDown' ? 5 : -5)));
                setActiveId(enabled[to]?.item.id ?? null);
                break;
            }
            case 'Enter':
                if (activeRow) {
                    e.preventDefault();
                    choose(activeRow.item);
                }
                break;
            case 'Backspace':
                if (search === '' && stack.length > 0) {
                    e.preventDefault();
                    popPage();
                }
                break;
        }
    };
    // Announce the number of results once results settle.
    const [announcement, setAnnouncement] = useState('');
    const showEmpty = query !== '' && rows.length === 0 && !loading && failed.length === 0;
    useEffect(() => {
        if (!open)
            return;
        const t = setTimeout(() => {
            if (loading)
                setAnnouncement(messages.loading);
            else if (query === '' && !page)
                setAnnouncement('');
            else
                setAnnouncement(rows.length ? messages.resultCount(rows.length) : messages.empty);
        }, 150);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loading, rows.length, query, pageKey]);
    const defaultFooter = (_jsxs("div", { className: "mn-command-palette__hints", "aria-hidden": true, children: [_jsxs("span", { children: [_jsx(Kbd, { children: "\u2191" }), _jsx(Kbd, { children: "\u2193" }), " ", messages.navigate] }), _jsxs("span", { children: [_jsx(Kbd, { children: "\u21B5" }), " ", messages.select] }), _jsxs("span", { children: [_jsx(Kbd, { children: "Esc" }), " ", messages.close] })] }));
    const footerContent = footer === undefined ? defaultFooter : footer;
    return (_jsxs(Dialog, { open: open, onOpenChange: setOpen, children: [trigger ? _jsx(DialogTrigger, { asChild: true, children: trigger }) : null, _jsxs(DialogContent, { ref: ref, "aria-describedby": undefined, closeLabel: null, escapeGuard: false, fullScreenOnPhone: true, className: cx('mn-command-palette', className), "data-page": pageKey ?? undefined, onOpenAutoFocus: (e) => {
                    e.preventDefault();
                    inputRef.current?.focus();
                }, onEscapeKeyDown: (e) => {
                    // Escape leaves a nested page first, then closes.
                    if (stack.length > 0) {
                        e.preventDefault();
                        popPage();
                    }
                }, children: [_jsx(DialogTitle, { className: "mn-visually-hidden", children: messages.title }), _jsxs("div", { className: "mn-command-palette__search", children: [_jsx("span", { className: "mn-command-palette__search-icon", "aria-hidden": true, children: loading ? _jsx(Spinner, { size: "sm" }) : _jsx(SearchGlyph, {}) }), page ? (_jsxs("button", { type: "button", className: "mn-command-palette__page", "aria-label": messages.back(page.title), onClick: popPage, children: [page.title, _jsx(ChevronRightGlyph, {})] })) : null, _jsx("input", { ref: inputRef, type: "text", role: "combobox", "aria-expanded": true, "aria-controls": listId, "aria-activedescendant": activeDomId, "aria-autocomplete": "list", "aria-label": page ? `${messages.title}: ${page.title}` : messages.title, autoComplete: "off", autoCorrect: "off", spellCheck: false, enterKeyHint: "go", className: "mn-command-palette__input", placeholder: page?.placeholder ?? messages.placeholder, value: search, onChange: (e) => setSearch(e.target.value), onKeyDown: onKeyDown })] }), _jsx("div", { ref: listRef, id: listId, role: "listbox", "aria-label": messages.results, className: "mn-command-palette__list", children: sections.map((section, sectionIndex) => {
                            const headingId = `${listId}-heading-${sectionIndex}`;
                            const options = section.rows.map(({ item, domId }) => (
                            // The input keeps focus and handles every key (aria-activedescendant),
                            // so options are deliberately not focusable.
                            // monarch-ignore-next-line a11y/click-without-key -- aria-activedescendant pattern: the input handles all keys
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                            _jsxs("div", { id: domId, role: "option", tabIndex: -1, "aria-selected": domId === activeDomId, "aria-disabled": item.disabled || undefined, "data-highlighted": domId === activeDomId || undefined, "data-tone": item.tone, className: "mn-command-palette__item", onPointerMove: () => {
                                    if (!item.disabled && activeId !== item.id)
                                        setActiveId(item.id);
                                }, onMouseDown: (e) => e.preventDefault(), onClick: () => choose(item), children: [item.icon ? (_jsx("span", { className: "mn-command-palette__icon", "aria-hidden": true, children: item.icon })) : null, _jsxs("span", { className: "mn-command-palette__text", children: [_jsx("span", { className: "mn-command-palette__label", children: item.label }), item.description ? (_jsx("span", { className: "mn-command-palette__description", children: item.description })) : null] }), item.shortcut ? (_jsx("span", { className: "mn-command-palette__shortcut", "aria-hidden": true, children: shortcutKeys(item.shortcut).map((k, i) => (_jsx(Kbd, { children: k }, i))) })) : null, item.page ? (_jsx("span", { className: "mn-command-palette__chevron", "aria-hidden": true, children: _jsx(ChevronRightGlyph, {}) })) : null] }, `${section.key}:${item.id}`)));
                            return section.heading ? (_jsxs("div", { role: "group", "aria-labelledby": headingId, className: "mn-command-palette__group", children: [_jsx("div", { id: headingId, role: "presentation", className: "mn-command-palette__heading", children: section.heading }), options] }, section.key)) : (_jsx("div", { role: "presentation", className: "mn-command-palette__group", children: options }, section.key));
                        }) }), showEmpty ? (_jsx("div", { className: "mn-command-palette__empty", children: emptyState ?? messages.empty })) : null, failed.map((source) => (_jsxs("div", { className: "mn-command-palette__error", children: [_jsx("span", { children: messages.sourceError(source.group) }), _jsx("button", { type: "button", className: "mn-command-palette__retry", onClick: () => {
                                    setRetryCount((n) => n + 1);
                                    inputRef.current?.focus();
                                }, children: messages.retry })] }, source.id))), _jsx("div", { className: "mn-visually-hidden", role: "status", "aria-live": "polite", "aria-atomic": "true", children: announcement }), footerContent !== null ? (_jsx("div", { className: "mn-command-palette__footer", children: footerContent })) : null] })] }));
});
//# sourceMappingURL=command-palette.js.map